import {
  DEFAULT_DATE_FORMAT_FNS,
  SchoolYear,
  StudentRegistration,
  TimetableStructure,
} from '@schooly/api';
import { SchoolPropertyType } from '@schooly/constants';
import { format } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import { isOpenedRegistration } from '../../../../components/common/ProfileModal/utils';
import { isRejectedEnrollment } from '../../../../components/common/StudentRegistration/utils';

export type AttendanceViewMode = 'session' | 'per-period';

type UseAttendanceTableControlsParams = {
  perPeriodAvailable: boolean;
  schoolYears: SchoolYear[];
  defaultValidity: SchoolYear | undefined;
  structures: TimetableStructure[];
  enrollments: StudentRegistration[];
};

type UseAttendanceTableControlsResult = {
  viewMode: AttendanceViewMode;
  setViewMode: Dispatch<SetStateAction<AttendanceViewMode>>;
  availableYears: SchoolYear[];
  defaultYear: SchoolYear | undefined;
  selectedYear: SchoolYear | undefined;
  setSchoolYear: (year: SchoolYear | undefined) => void;
  structures: TimetableStructure[];
  selectedStructure: TimetableStructure | undefined;
  setSelectedStructureId: (id: string) => void;
};

export const useAttendanceTableControls = ({
  perPeriodAvailable,
  schoolYears,
  defaultValidity,
  structures,
  enrollments,
}: UseAttendanceTableControlsParams): UseAttendanceTableControlsResult => {
  const today = format(new Date(), DEFAULT_DATE_FORMAT_FNS);

  const [viewMode, setViewMode] = useState<AttendanceViewMode>(
    perPeriodAvailable ? 'per-period' : 'session',
  );
  const [schoolYear, setSchoolYear] = useState<SchoolYear | undefined>();
  const [selectedStructureId, setSelectedStructureId] = useState<string | null>(null);

  const selectSchoolYear = useCallback((year: SchoolYear | undefined) => {
    setSchoolYear(year);
    setSelectedStructureId(null);
  }, []);

  // Only past and current years are shown
  const availableYears = useMemo(
    () => schoolYears.filter((year) => year.start <= today),
    [schoolYears, today],
  );

  const latestAvailableYear = useMemo(
    () =>
      availableYears.reduce<SchoolYear | undefined>(
        (latest, year) => (!latest || year.start > latest.start ? year : latest),
        undefined,
      ),
    [availableYears],
  );

  const defaultYear =
    defaultValidity && defaultValidity.start <= today ? defaultValidity : latestAvailableYear;

  const selectedYear = schoolYear ?? defaultYear;

  const selectedStructure = useMemo(() => {
    const structureBySelectedId = structures.find(
      (structure) => structure.id === selectedStructureId,
    );

    if (structureBySelectedId) return structureBySelectedId;

    const registration = enrollments.find(
      (enrollment) =>
        enrollment.school_year.id === selectedYear?.id &&
        !isRejectedEnrollment(enrollment) &&
        isOpenedRegistration(enrollment.statuses),
    );
    const ageGroupId = registration?.school_properties.find(
      (property) => property.type === SchoolPropertyType.AgeGroup,
    )?.id;

    return (
      structures.find(
        (structure) => ageGroupId !== undefined && structure.age_group_ids.includes(ageGroupId),
      ) ?? structures[0]
    );
  }, [enrollments, selectedStructureId, selectedYear, structures]);

  return {
    viewMode,
    setViewMode,
    availableYears,
    defaultYear,
    selectedYear,
    setSchoolYear: selectSchoolYear,
    structures,
    selectedStructure,
    setSelectedStructureId,
  };
};
